import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Field } from '../components/Field';
import { ViewHeader } from '../components/ViewHeader';
import { EntityRow } from '../components/EntityRow';
import { EmptyState } from '../components/EmptyState';
import { useWorkspace } from '../hooks/useWorkspace';
import { useLang } from '../hooks/useLang';
import { useEntityList } from '../hooks/useEntityList';
import { useToast } from '../components/Toast';
import { LIMITS, STARTER_TYPES } from '../../domain/constants';
import { disciplinesFor } from '../../domain/disciplines';
import {
  athleteName,
  newStarter,
  sexMismatches,
  starterSex,
  starterIsSG,
  starterName,
  starterClub,
  starterClubFull,
} from '../../domain/workspace';
import { t } from '../../i18n';
import { starterTypeLabel } from '../../i18n/labels';
import type { WorkspaceStore } from '../../domain/store';
import type { Athlete, Workspace, Sex, Starter, StarterType } from '../../types';

const NONE = ' none';

/** `starter.derived.*` translation strings deliberately embed `<b>` tags
 *  (see de.ts) — the vanilla version rendered them via `innerHTML` after
 *  escaping the interpolated values first; this does the same for the JSX
 *  port, via `dangerouslySetInnerHTML`, so those tags render as real
 *  formatting instead of literal text. */
function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function sexLabel(sex: Sex): string {
  return sex === 'w' ? t('common.sexWoman') : t('common.sexMan');
}

function applyType(s: Starter, nextType: StarterType): void {
  s.type = nextType;
  const options = disciplinesFor(nextType);
  if (!options.some((d) => d.de === s.discipline)) {
    const first = options[0]!;
    s.discipline = first.de;
    s.disciplineEn = first.en;
  }
  const max = STARTER_TYPES[nextType].riders;
  if (s.athleteIds.length > max) s.athleteIds = s.athleteIds.slice(0, max);
}

function applyDiscipline(s: Starter): void {
  const match = disciplinesFor(s.type).find((d) => d.de === s.discipline);
  s.disciplineEn = match?.en ?? '';
}

export function StarterView({ store, hidden }: { store: WorkspaceStore; hidden: boolean }) {
  const workspace = useWorkspace(store);
  useLang();
  const toast = useToast();
  const { editing, startEditing, stopEditing, stopEditingIf } = useEntityList();
  const { starters } = workspace;

  function handleAdd(): void {
    const s = newStarter();
    store.update((p) => p.starters.push(s));
    startEditing(s.id);
  }

  function handleDelete(id: string): void {
    const used = store.workspace.entries.filter((e) => e.starterId === id).length;
    if (used > 0 && !confirm(t('starter.deleteConfirm', { used }))) return;
    store.update((p) => {
      p.starters = p.starters.filter((s) => s.id !== id);
      // An Entry without its starter has nothing left to register — drop it
      // along with the starter rather than leave a dangling reference.
      p.entries = p.entries.filter((e) => e.starterId !== id);
    });
    stopEditingIf(id);
    toast(t('starter.deleted'));
  }

  return (
    <section className="mx-auto flex max-w-[1180px] flex-col gap-3 p-4" hidden={hidden}>
      <ViewHeader title={t('starter.title')} lede={t('starter.lede')} />

      {starters.length === 0 ? (
        <EmptyState title={t('starter.empty.title')} text={t('starter.empty.text')} />
      ) : (
        starters.map((s) =>
          s.id === editing ? (
            <StarterForm key={s.id} s={s} workspace={workspace} store={store} toast={toast} onDone={stopEditing} onDelete={() => handleDelete(s.id)} />
          ) : (
            <StarterRow key={s.id} s={s} workspace={workspace} onEdit={() => startEditing(s.id)} />
          ),
        )
      )}
      <Button className="w-full" onClick={handleAdd}>
        {t('starter.add')}
      </Button>
    </section>
  );
}

function StarterRow({ s, workspace, onEdit }: { s: Starter; workspace: Workspace; onEdit: () => void }) {
  const need = STARTER_TYPES[s.type].riders;
  const have = s.athleteIds.length;
  const club = starterClub(workspace, s);
  const wrongSex = sexMismatches(workspace, s);

  return (
    <EntityRow
      title={starterName(workspace, s)}
      sub={`${s.discipline}${club ? ` · ${club}` : ''}`}
      tags={
        <>
          {have !== need ? <Badge variant="warn">{t('starter.riderTag', { have, need })}</Badge> : null}
          {starterIsSG(workspace, s) ? <Badge>{t('starter.sgTag')}</Badge> : null}
          {s.clubAlias.trim() !== '' ? <Badge>{t('starter.aliasTag')}</Badge> : null}
          {wrongSex.length > 0 ? <Badge variant="warn">{t('starter.sexMismatchTag')}</Badge> : null}
        </>
      }
      actions={
        <Button variant="outline" size="sm" onClick={onEdit}>
          {t('common.edit')}
        </Button>
      }
    />
  );
}

function AthletePicker({
  s,
  athletes,
  store,
  toast,
}: {
  s: Starter;
  athletes: Athlete[];
  store: WorkspaceStore;
  toast: (message: string) => void;
}) {
  if (athletes.length === 0) {
    return (
      <Alert>
        <AlertDescription dangerouslySetInnerHTML={{ __html: t('starter.personen.needSportler') }} />
      </Alert>
    );
  }
  const label = (a: Athlete): string => `${athleteName(a)} (${a.uciId || t('common.uciIdMissing')} · ${sexLabel(a.sex)})`;

  if (STARTER_TYPES[s.type].riders === 1) {
    const current = s.athleteIds[0] ?? '';
    return (
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="starter-single-athlete">{t('starter.personen.pick')}</Label>
        <Select
          value={current === '' ? NONE : current}
          onValueChange={(value) => {
            const next = value === NONE ? '' : value;
            store.update(() => {
              s.athleteIds = next ? [next] : [];
            });
          }}
        >
          <SelectTrigger id="starter-single-athlete" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('starter.personen.pickPlaceholder')}</SelectItem>
            {athletes.map((a) => (
              <SelectItem key={a.id} value={a.id}>
                {label(a)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  function toggle(athleteId: string, checked: boolean): void {
    if (checked) {
      const max = STARTER_TYPES[s.type].riders;
      if (s.athleteIds.length >= max) {
        toast(t('starter.riderLimitToast', { label: starterTypeLabel(s.type), max }));
        return;
      }
      store.update(() => s.athleteIds.push(athleteId));
    } else {
      store.update(() => {
        s.athleteIds = s.athleteIds.filter((x) => x !== athleteId);
      });
    }
  }

  return (
    <div className="flex flex-col gap-1.5">
      {athletes.map((a) => (
        <label className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm has-[:checked]:border-ring has-[:checked]:bg-secondary" key={a.id}>
          <Checkbox checked={s.athleteIds.includes(a.id)} onCheckedChange={(checked) => toggle(a.id, checked === true)} />
          <span className="flex-1">
            {athleteName(a)}
            <small className="block font-mono text-[11px] text-muted-foreground">
              {a.uciId || t('common.uciIdMissing')} · {a.nation} · {sexLabel(a.sex)}
            </small>
          </span>
        </label>
      ))}
    </div>
  );
}

function StarterForm({
  s,
  workspace,
  store,
  toast,
  onDone,
  onDelete,
}: {
  s: Starter;
  workspace: Workspace;
  store: WorkspaceStore;
  toast: (message: string) => void;
  onDone: () => void;
  onDelete: () => void;
}) {
  const typeOptions = (Object.keys(STARTER_TYPES) as StarterType[]).map((k) => [k, starterTypeLabel(k)] as const);
  const disciplineOptions = disciplinesFor(s.type).map((d) => [d.de, d.de] as const);
  const athletes = workspace.athletes;
  const fullClub = starterClubFull(workspace, s);
  const club = starterClub(workspace, s);
  const isSG = starterIsSG(workspace, s);
  const sex = starterSex(workspace, s);
  const wrongSex = sexMismatches(workspace, s);
  const hasAlias = s.clubAlias.trim() !== '';
  // Whether the raw, un-aliased combination overruns the limit — this
  // decides whether to *offer* the alias field at all (kept visible once an
  // alias exists, even if the roster later shrinks back under the limit, so
  // the coach can still see and clear it), and whether `club` below is
  // actually WeBo-style auto-truncated rather than the full joined name.
  const fullOverLimit = fullClub.length > LIMITS.club;
  // Whether the value that will actually be exported — the alias, once set
  // — still overruns the limit. With no alias, `starterClub()` always
  // auto-truncates to fit, so this can only fire for a hand-typed alias
  // that is itself too long (the input's own maxLength normally prevents
  // that, but an imported workspace file could still carry one).
  const effectiveOverLimit = hasAlias && club.length > LIMITS.club;

  return (
    <Card className="ring-2 ring-ring/40">
      <CardHeader>
        <CardTitle>{t('starter.form.title')}</CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field
          scope="starter"
          store={store}
          target={s}
          spec={{ label: t('starter.field.type'), key: 'type', required: true, options: typeOptions, hint: '' }}
          onCommit={(value) => applyType(s, value as StarterType)}
        />
        <Field
          scope="starter"
          store={store}
          target={s}
          spec={{ label: t('starter.field.discipline'), key: 'discipline', required: true, options: disciplineOptions, hint: '' }}
          onCommit={() => applyDiscipline(s)}
        />
        <Field
          scope="starter"
          store={store}
          target={s}
          spec={{ label: t('starter.field.startername'), key: 'name', maxLength: LIMITS.starterName, wide: true, placeholder: starterName(workspace, s), hint: t('starter.field.startername.hint') }}
        />
      </CardContent>
      <Separator />
      <CardContent className="flex flex-col gap-3">
        <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {t('starter.personen.title')} <Badge>{t('starter.riderTag', { have: s.athleteIds.length, need: STARTER_TYPES[s.type].riders })}</Badge>
        </p>
        <AthletePicker s={s} athletes={athletes} store={store} toast={toast} />

        <Alert>
          <AlertDescription>
            <span
              dangerouslySetInnerHTML={{
                __html:
                  t('starter.derived.club', { club: escapeHtml(club || '—') }) +
                  (isSG ? t('starter.derived.sg') : '') +
                  t('starter.derived.sex', { sex: escapeHtml(sexLabel(sex)) }),
              }}
            />
            <br />
            {hasAlias
              ? t('starter.derived.note.alias', { full: fullClub })
              : fullOverLimit
                ? t('starter.derived.note.autoTruncated', { max: LIMITS.club })
                : t('starter.derived.note')}
          </AlertDescription>
        </Alert>
        {!hasAlias && fullOverLimit ? (
          <Alert>
            <AlertDescription>{t('starter.clubAlias.autoTruncated', { full: fullClub, len: fullClub.length, max: LIMITS.club })}</AlertDescription>
          </Alert>
        ) : null}
        {effectiveOverLimit ? (
          <Alert variant="destructive">
            <AlertDescription>{t('starter.clubAlias.aliasStillOverLimit', { len: club.length, max: LIMITS.club })}</AlertDescription>
          </Alert>
        ) : null}
        {fullOverLimit || hasAlias ? (
          <Field
            scope="starter"
            store={store}
            target={s}
            spec={{ label: t('starter.field.clubAlias'), key: 'clubAlias', maxLength: LIMITS.club, wide: true, hint: t('starter.field.clubAlias.hint') }}
          />
        ) : null}
        {wrongSex.length > 0 ? (
          <Alert variant="destructive">
            <AlertDescription>
              {t(wrongSex.length === 1 ? 'starter.sexMismatch.one' : 'starter.sexMismatch.many', {
                names: wrongSex.map((a) => athleteName(a)).join(', '),
              })}
            </AlertDescription>
          </Alert>
        ) : null}
      </CardContent>
      <Separator />
      <CardContent className="flex flex-col gap-4">
        <p className="flex items-center gap-2 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          {t('starter.personalBest.title')} <Badge>{t('common.optional')}</Badge>
        </p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field scope="personalBest" store={store} target={s.personalBest} spec={{ label: t('starter.field.points'), key: 'points', maxLength: LIMITS.personalBest, mono: true, placeholder: '81.05' }} />
          <Field scope="personalBest" store={store} target={s.personalBest} spec={{ label: t('starter.field.date'), key: 'date', type: 'date', hint: '' }} />
          <Field scope="personalBest" store={store} target={s.personalBest} spec={{ label: t('starter.field.venue'), key: 'venue', maxLength: LIMITS.city }} />
        </div>
        <Field scope="starter" store={store} target={s} spec={{ label: t('starter.field.scoringReportEmail'), key: 'scoringReportEmail', maxLength: LIMITS.scoringReportEmail, type: 'email' }} />
      </CardContent>
      <CardFooter>
        <Button onClick={onDone}>{t('common.done')}</Button>
        <Button variant="destructive" onClick={onDelete}>
          {t('common.delete')}
        </Button>
      </CardFooter>
    </Card>
  );
}
